import type { FevDeAlistamiento } from '@/lib/supabase/tipos-bd'
import { fecha, fechaHora } from '@/shared/utilidades/formato'

import { useReintentarFacturacion } from './useReintentarFacturacion'
import estilos from './PaginaAlistamientos.module.css'

/**
 * EN QUÉ VA EL PASO A FEV (0252), dicho como lo entiende bodega.
 *
 * DOS CAMINOS A LA MISMA FEV. El motor que convierte la COT en FV desde la
 * cola se está construyendo del lado del worker (Johan: «sí habrá pasar a
 * FEV, lo estamos construyendo allá»). Mientras llega —y también después—,
 * si alguien factura a mano en World Office, la app la amarra sola (0257,
 * 0258) y esto pasa a «Facturada»: por el enlace de World Office si se
 * convirtió desde la COT, o por sus productos si se digitó.
 */
export function EstadoDeFev({
  fev,
  cotizacionId,
}: {
  fev: FevDeAlistamiento
  cotizacionId: string
}) {
  const reintentar = useReintentarFacturacion()
  const quien = fev.pedida_por ? ` por ${fev.pedida_por.split(' ').slice(0, 2).join(' ')}` : ''
  const cuando = fev.pedida_en ? ` el ${fechaHora(fev.pedida_en)}` : ''
  // «3:20 p. m.» ya trae su punto: no se le pone otro.
  const cierre = cuando.endsWith('.') ? '' : '.'

  // 0296: se decidió como remisión en la cotización.
  if (fev.documento === 'remision')
    return (
      <EstadoDeRemision fev={fev} quien={quien} cuando={cuando} />
    )

  // 0272: sin IVA, World Office no la factura; pasa a FEV solo en la app.
  if (fev.estado === 'sin_iva')
    return (
      <div className={`${estilos.fev} ${estilos.fevHecha}`}>
        <p className={estilos.fevTitulo}>En FEV · sin IVA, solo en la app</p>
        <p>
          Pasada{quien}
          {cuando}{cierre} No lleva IVA, así que World Office no la factura: se cobra con la cotización. En
          cartera cuenta como facturada.
        </p>
      </div>
    )
  if (fev.estado === 'facturada')
    return (
      <div className={`${estilos.fev} ${estilos.fevHecha}`}>
        <p className={estilos.fevTitulo}>Facturada · {fev.numero ?? 'FEV'}</p>
        <p>
          {fev.como === 'motor'
            ? 'La facturó el motor de World Office'
            : 'Se facturó en World Office'}
          {fev.facturada_en ? ` el ${fecha(fev.facturada_en)}` : ''}. Ya cuenta en cartera.
        </p>
        {/* World Office la presenta a la DIAN después, por tandas (0259). */}
        <p className={fev.presentada ? estilos.fevDian : estilos.fevDianPendiente}>
          {fev.presentada
            ? `Presentada a la DIAN${fev.presentada_en ? ` el ${fecha(fev.presentada_en)}` : ''}.`
            : 'Pendiente de presentar a la DIAN: la presentan desde World Office por tandas.'}
        </p>
      </div>
    )
  if (fev.estado === 'error')
    return (
      <div className={`${estilos.fev} ${estilos.fevError}`}>
        <p className={estilos.fevTitulo}>World Office no pudo facturarla</p>
        <p>{fev.error ?? 'Sin detalle del error.'}</p>
        {fev.reintentable ? (
          <>
            <button
              type="button"
              className={estilos.fevReintentar}
              onClick={() => reintentar.mutate(cotizacionId)}
              disabled={reintentar.isPending}
            >
              {reintentar.isPending ? 'Mandando…' : 'Volver a intentar'}
            </button>
            {/resoluci/i.test(fev.error ?? '') && (
              <p>Antes, avísale a contabilidad: falta la resolución de facturación vigente.</p>
            )}
          </>
        ) : (
          <p>No se arregla volviendo a intentar: avísale a sistemas (WORKERS MEGA).</p>
        )}
        {reintentar.error && <p>{reintentar.error.message}</p>}
      </div>
    )
  if (fev.estado === 'esperando_world_office')
    return (
      <div className={estilos.fev}>
        <p className={estilos.fevTitulo}>Lista para FEV · esperando a World Office</p>
        <p>
          Marcada{quien}
          {cuando}{cierre} Su cambio va en camino a World Office; en cuanto se confirme, entra sola a la
          cola de facturación.
        </p>
      </div>
    )
  if (fev.estado === 'procesando')
    return (
      <div className={estilos.fev}>
        <p className={estilos.fevTitulo}>Facturando en World Office…</p>
        <p>El motor la está convirtiendo en factura. Tarda unos segundos.</p>
      </div>
    )
  return (
    <div className={estilos.fev}>
      <p className={estilos.fevTitulo}>Lista para FEV · en la cola de facturación</p>
      <p>
        Marcada{quien}
        {cuando}{cierre} La convierte en factura el motor de World Office. Si se factura antes a mano en
        World Office, la app la amarra sola. Aquí sale el número cuando llegue.
      </p>
    </div>
  )
}

/**
 * EN QUÉ VA LA REMISIÓN (0296). La remisión desde la app va apagada hasta
 * que contabilidad defina cómo se factura una remisión: mientras, bodega la
 * deja lista y queda en espera.
 */
function EstadoDeRemision({
  fev,
  quien,
  cuando,
}: {
  fev: FevDeAlistamiento
  quien: string
  cuando: string
}) {
  const cierre = cuando.endsWith('.') ? '' : '.'
  if (fev.estado === 'remitida')
    return (
      <div className={`${estilos.fev} ${estilos.fevHecha}`}>
        <p className={estilos.fevTitulo}>Remitida · {fev.numero ?? 'remisión'}</p>
        <p>
          World Office la convirtió en remisión
          {fev.facturada_en ? ` el ${fecha(fev.facturada_en)}` : ''}. El material sale de bodega
          con ella.
        </p>
        <FacturaDeLaRemision fev={fev} />
      </div>
    )
  if (fev.estado === 'error')
    return (
      <div className={`${estilos.fev} ${estilos.fevError}`}>
        <p className={estilos.fevTitulo}>World Office no pudo hacer la remisión</p>
        <p>{fev.error ?? 'Sin detalle del error.'}</p>
        <p>Avísale a sistemas (WORKERS MEGA).</p>
      </div>
    )
  if (fev.estado === 'en_espera')
    return (
      <div className={estilos.fev}>
        <p className={estilos.fevTitulo}>Lista para remisión · en espera</p>
        <p>
          Marcada{quien}
          {cuando}{cierre} La remisión desde la app todavía no está encendida en World Office: en cuanto
          se encienda, sale sola. Si hay que despachar ya, hágala a mano en World Office desde la
          cotización.
        </p>
      </div>
    )
  if (fev.estado === 'procesando')
    return (
      <div className={estilos.fev}>
        <p className={estilos.fevTitulo}>Haciendo la remisión en World Office…</p>
        <p>Tarda unos segundos.</p>
      </div>
    )
  return (
    <div className={estilos.fev}>
      <p className={estilos.fevTitulo}>
        Lista para remisión ·{' '}
        {fev.estado === 'esperando_world_office'
          ? 'esperando a World Office'
          : 'en la cola de World Office'}
      </p>
      <p>
        Marcada{quien}
        {cuando}{cierre} Aquí sale el número de la remisión cuando llegue.
      </p>
    </div>
  )
}

/**
 * LA FACTURA DE UNA REMISIÓN (0297, 0298). Johan: «desde la app no se puede
 * facturar la remisión». Se factura en World Office, donde contabilidad hace
 * en el mismo momento el reverso de la remisión. La app solo la reconoce
 * cuando llega y recuerda ese reverso: sin él, la mercancía sale dos veces y
 * el cliente queda debiendo el doble.
 */
function FacturaDeLaRemision({ fev }: { fev: FevDeAlistamiento }) {
  const f = fev.factura

  if (f?.estado === 'facturada')
    return (
      <>
        <p>
          <b>Facturada{f.numero ? ` · ${f.numero}` : ''}</b>, desde la remisión en World Office.
        </p>
        <p className={estilos.fevDianPendiente}>
          Contabilidad: reversar la remisión (Clientes remisiones, IVA transitorio y la diferencia
          de inventario) y reingresar sus unidades. Si no, la mercancía queda descontada dos veces.
        </p>
      </>
    )
  // Sin factura todavía no se dice nada más (Johan: «quítale esa nota»).
  return null
}
